import React, { useState, useEffect } from 'react';
import { useBusinessStore } from '@/stores/useBusinessStore';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
    ArrowLeft, Loader2, Upload, X, Package, DollarSign,
    BarChart2, Tag, Layers, Smartphone, Camera, AlertCircle,
    Info, ChevronDown, ChevronUp, Star, Barcode, Trash2
} from 'lucide-react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/contexts/ToastContext';
import mediaUpload from '@/services/mediaUpload';
import { extractImageUrls } from '@/config/media';

const productSchema = z.object({
    name: z.string().min(1, 'Product name is required'),
    description: z.string().optional(),
    category: z.string().min(1, 'Category is required'),
    price: z.string().min(1, 'Selling price is required'),
    cost_price: z.string().min(1, 'Cost price is required'),
    quantity_in_stock: z.string().min(1, 'Stock quantity is required'),
    low_stock_threshold: z.string().optional(),
    sku: z.string().optional(),
    barcode: z.string().optional(),
    is_active: z.boolean(),
    unit_type: z.string().optional(),
    bulk_unit_size: z.string().optional(),
    bulk_unit_cost: z.string().optional(),
});

type ProductFormData = z.infer<typeof productSchema>;

const CATEGORIES = [
    'General', 'Electronics', 'Fashion & Clothing', 'Food & Beverages',
    'Home & Living', 'Beauty & Personal Care', 'Health & Wellness',
    'Sports & Outdoors', 'Automotive', 'Stationery', 'Toys & Games',
    'Agriculture', 'Hardware & Tools', 'Other',
];

const InputField: React.FC<{
    label: string;
    error?: string;
    hint?: string;
    prefix?: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
}> = ({ label, error, hint, prefix, icon, children }) => (
    <div className="space-y-1.5">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
            {label}
        </label>
        <div className={`relative flex items-center rounded-xl border-2 transition-colors ${
            error
                ? 'border-red-400 bg-red-50 dark:bg-red-900/10'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 focus-within:border-primary dark:focus-within:border-golden'
        }`}>
            {icon && (
                <div className="pl-3 text-gray-400 dark:text-gray-500 flex-shrink-0">
                    {icon}
                </div>
            )}
            {prefix && (
                <span className="pl-3 pr-1 text-sm font-medium text-gray-500 dark:text-gray-400 flex-shrink-0">
                    {prefix}
                </span>
            )}
            <div className="flex-1">
                {children}
            </div>
        </div>
        {error && (
            <p className="flex items-center gap-1 text-xs text-red-500">
                <AlertCircle size={12} /> {error}
            </p>
        )}
        {hint && !error && (
            <p className="text-xs text-gray-400 dark:text-gray-500">{hint}</p>
        )}
    </div>
);

const inputClass = "w-full px-3 py-3 bg-transparent text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none text-sm";

const SectionCard: React.FC<{
    title: string;
    subtitle?: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    collapsible?: boolean;
    defaultOpen?: boolean;
}> = ({ title, subtitle, icon, children, collapsible = false, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
            <div
                className={`flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 ${collapsible ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors' : ''}`}
                onClick={collapsible ? () => setOpen(o => !o) : undefined}
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10 dark:bg-golden/10 text-primary dark:text-golden">
                        {icon}
                    </div>
                    <div>
                        <h2 className="text-base font-bold text-gray-800 dark:text-white">{title}</h2>
                        {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
                    </div>
                </div>
                {collapsible && (
                    <div className="text-gray-400">
                        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                )}
            </div>
            {(!collapsible || open) && (
                <div className="px-6 py-5">
                    {children}
                </div>
            )}
        </div>
    );
};

export const ProductForm: React.FC = () => {
    const { productId, businessId: urlBusinessId } = useParams();
    const navigate = useNavigate();
    const { activeBusiness, createProduct, updateProduct, deleteProduct, products, loading, error: storeError } = useBusinessStore();
    const toast = useToast();
    const isEditMode = !!productId;
    const targetBusinessId = urlBusinessId || activeBusiness?.id;

    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<ProductFormData>({
        resolver: zodResolver(productSchema),
        defaultValues: {
            is_active: true,
            category: 'General',
            low_stock_threshold: '5',
            unit_type: '',
            bulk_unit_size: '',
            bulk_unit_cost: ''
        }
    });

    const price = watch('price');
    const costPrice = watch('cost_price');
    const margin = price && costPrice
        ? Math.round(((parseFloat(price) - parseFloat(costPrice)) / parseFloat(price)) * 100)
        : null;

    useEffect(() => {
        if (isEditMode && products.length > 0) {
            const product = products.find(p => p.id === productId);
            if (product) {
                reset({
                    name: product.name,
                    description: product.description,
                    category: product.category,
                    price: String(product.price),
                    cost_price: String(product.cost_price || 0),
                    quantity_in_stock: String(product.quantity_in_stock),
                    low_stock_threshold: String(product.low_stock_threshold || 5),
                    sku: product.sku || '',
                    barcode: product.barcode || '',
                    is_active: product.is_active ?? true,
                    unit_type: product.unit_type || '',
                    bulk_unit_size: product.bulk_unit_size ? String(product.bulk_unit_size) : '',
                    bulk_unit_cost: product.bulk_unit_cost ? String(product.bulk_unit_cost) : ''
                });
                if (product.media_urls) {
                    // Use extractImageUrls to properly resolve CDN URLs
                    const resolved = extractImageUrls(product.media_urls);
                    setPreviewUrls(resolved.length > 0 ? resolved : (
                        Array.isArray(product.media_urls) ? product.media_urls : []
                    ));
                }
            }
        }
    }, [isEditMode, products, productId, reset]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files);
            const newPreviews = files.map(file => URL.createObjectURL(file));
            setImageFiles(prev => [...prev, ...files]);
            setPreviewUrls(prev => [...prev, ...newPreviews]);
        }
    };

    const removeImage = (index: number) => {
        setPreviewUrls(prev => prev.filter((_, i) => i !== index));
        setImageFiles(prev => prev.filter((_, i) => i !== index));
    };

    const onSubmit = async (data: ProductFormData) => {
        if (!targetBusinessId) {
            toast.error('Error', 'No business selected. Please select a business first.');
            return;
        }
        if (submitting) return;
        setSubmitting(true);

        try {
            let finalMediaUrls = previewUrls.filter(url => !url.startsWith('blob:'));

            if (imageFiles.length > 0) {
                setUploading(true);
                const uploadedUrls: string[] = [];
                for (const file of imageFiles) {
                    try {
                        const url = await mediaUpload.uploadImage(file, { folder: 'products' });
                        if (url) uploadedUrls.push(url);
                    } catch (e) {
                        console.error('Failed to upload image', e);
                        toast.error('Upload Error', 'Failed to upload one or more images');
                    }
                }
                setUploading(false);
                finalMediaUrls = [...finalMediaUrls, ...uploadedUrls];
            }

            const productPayload = {
                ...data,
                price: parseFloat(data.price),
                cost_price: parseFloat(data.cost_price),
                quantity_in_stock: parseInt(data.quantity_in_stock),
                low_stock_threshold: parseInt(data.low_stock_threshold || '5'),
                unit_type: data.unit_type || null,
                bulk_unit_size: data.bulk_unit_size ? parseFloat(data.bulk_unit_size) : null,
                bulk_unit_cost: data.bulk_unit_cost ? parseFloat(data.bulk_unit_cost) : null,
                media_urls: finalMediaUrls,
            };

            if (isEditMode && productId) {
                await updateProduct(targetBusinessId, productId, productPayload);
                toast.success('Updated!', 'Product updated successfully');
            } else {
                await createProduct(targetBusinessId, productPayload);
                toast.success('Created!', 'Product added to your catalogue');
            }

            navigate(`/business/${targetBusinessId}/products`);
        } catch (error: any) {
            console.error('Failed to save product:', error);
            toast.error('Error', error?.response?.data?.error || error?.message || 'Failed to save product');
        } finally {
            setSubmitting(false);
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!targetBusinessId || !productId) return;
        if (!window.confirm("Are you sure you want to delete this product? This action cannot be undone.")) return;
        
        try {
            setSubmitting(true);
            await deleteProduct(targetBusinessId, productId);
            toast.success("Deleted", "Product deleted successfully");
            navigate(`/business/${targetBusinessId}/products`);
        } catch (error: any) {
            toast.error("Error", error?.response?.data?.error || "Failed to delete product");
            setSubmitting(false);
        }
    };

    const isLoading = submitting || uploading;

    return (
        <div className="max-w-2xl mx-auto pb-24">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(targetBusinessId ? `/business/${targetBusinessId}/products` : '/business')}
                    className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-gray-700"
                >
                    <ArrowLeft size={20} className="text-gray-600 dark:text-gray-300" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {isEditMode ? 'Edit Product' : 'New Product'}
                    </h1>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5">
                        {isEditMode ? 'Update your product details' : 'Add a product to your catalogue'}
                    </p>
                </div>
            </div>

            {/* Mobile App Hint Banner */}
            <div className="flex items-start gap-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/40 dark:to-purple-950/40 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-4 mb-6">
                <div className="p-2 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                    <Smartphone size={20} />
                </div>
                <div>
                    <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-200">
                        📱 Use the Majengo Mobile App for faster stock management
                    </p>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                        Scan barcodes, bulk upload products, record stock counts and do full stocktakes from your phone — much faster than the web!
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {/* Basic Information */}
                <SectionCard
                    title="Basic Information"
                    subtitle="Name, category and description"
                    icon={<Package size={18} />}
                >
                    <div className="space-y-4">
                        <InputField label="Product Name *" error={errors.name?.message} icon={<Tag size={16} />}>
                            <input
                                {...register('name')}
                                className={inputClass}
                                placeholder="e.g., Colgate Toothpaste 100ml"
                            />
                        </InputField>

                        <InputField label="Category *" error={errors.category?.message}>
                            <select {...register('category')} className={`${inputClass} pr-3`}>
                                {CATEGORIES.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </InputField>

                        <InputField label="Description" hint="Helps customers understand the product (optional)">
                            <textarea
                                {...register('description')}
                                rows={3}
                                className={`${inputClass} resize-none`}
                                placeholder="Describe your product..."
                            />
                        </InputField>
                    </div>
                </SectionCard>

                {/* Pricing & Inventory */}
                <SectionCard
                    title="Pricing & Inventory"
                    subtitle="Set your prices and stock levels"
                    icon={<DollarSign size={18} />}
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <InputField
                                label="Selling Price *"
                                error={errors.price?.message}
                                prefix="KES"
                            >
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...register('price')}
                                    className={inputClass}
                                    placeholder="0.00"
                                />
                            </InputField>

                            <InputField
                                label="Cost Price *"
                                error={errors.cost_price?.message}
                                prefix="KES"
                                hint={margin !== null && !isNaN(margin) ? `${margin}% margin` : undefined}
                            >
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...register('cost_price')}
                                    className={inputClass}
                                    placeholder="0.00"
                                />
                            </InputField>
                        </div>

                        {margin !== null && !isNaN(margin) && (
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium ${
                                margin >= 20
                                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                    : margin >= 5
                                        ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                                        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                            }`}>
                                <Star size={14} />
                                {margin >= 20 ? '🟢 Great margin!' : margin >= 5 ? '🟡 Thin margin' : '🔴 Below cost or very low margin'}
                                {' '} — {margin}% profit margin
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <InputField
                                label="Stock Quantity *"
                                error={errors.quantity_in_stock?.message}
                                icon={<BarChart2 size={16} />}
                            >
                                <input
                                    type="number"
                                    min="0"
                                    {...register('quantity_in_stock')}
                                    className={inputClass}
                                    placeholder="0"
                                />
                            </InputField>

                            <InputField
                                label="Low Stock Alert"
                                hint="Alert when stock falls below"
                            >
                                <input
                                    type="number"
                                    min="0"
                                    {...register('low_stock_threshold')}
                                    className={inputClass}
                                    placeholder="5"
                                />
                            </InputField>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <InputField label="SKU" hint="Internal product code (optional)">
                                <input
                                    {...register('sku')}
                                    className={inputClass}
                                    placeholder="e.g., PROD-001"
                                />
                            </InputField>
                            <InputField label="Barcode" hint="Scan or type barcode (optional)" icon={<Barcode size={16} />}>
                                <input
                                    {...register('barcode')}
                                    className={inputClass}
                                    placeholder="e.g., 6001234567890"
                                />
                            </InputField>
                        </div>
                    </div>
                </SectionCard>

                {/* Wholesale & Bulk Settings */}
                <SectionCard
                    title="Wholesale & Bulk Settings"
                    subtitle="For products sold by weight, volume or bulk packs"
                    icon={<Layers size={18} />}
                    collapsible
                    defaultOpen={false}
                >
                    <div className="space-y-4">
                        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                            <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                                Use this section for products like cooking oil (sold in litres), sugar (kg), or items sold in wholesale cartons.
                            </p>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            <InputField label="Unit Type" hint="e.g., kg, litre, carton">
                                <input
                                    {...register('unit_type')}
                                    className={inputClass}
                                    placeholder="kg"
                                />
                            </InputField>

                            <InputField label="Items Per Unit" hint="e.g., 24 bottles per carton">
                                <input
                                    type="number"
                                    {...register('bulk_unit_size')}
                                    className={inputClass}
                                    placeholder="20"
                                />
                            </InputField>

                            <InputField label="Wholesale Price" prefix="KES" hint="Price per bulk pack">
                                <input
                                    type="number"
                                    {...register('bulk_unit_cost')}
                                    className={inputClass}
                                    placeholder="3500"
                                />
                            </InputField>
                        </div>
                    </div>
                </SectionCard>

                {/* Images */}
                <SectionCard
                    title="Product Images"
                    subtitle="Add photos to make your product stand out"
                    icon={<Camera size={18} />}
                >
                    <div className="space-y-4">
                        {/* Image grid */}
                        <div className="grid grid-cols-3 gap-3">
                            {previewUrls.map((url, index) => (
                                <div
                                    key={index}
                                    className="relative aspect-square bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden group border-2 border-gray-200 dark:border-gray-700"
                                >
                                    <img
                                        src={url}
                                        alt={`Preview ${index + 1}`}
                                        className="w-full h-full object-cover"
                                        onError={e => {
                                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f3f4f6"/%3E%3Ctext x="50" y="55" font-size="30" text-anchor="middle" fill="%23d1d5db"%3E📦%3C/text%3E%3C/svg%3E';
                                        }}
                                    />
                                    {index === 0 && (
                                        <div className="absolute top-1.5 left-1.5 bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                                            MAIN
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}

                            {/* Add image button */}
                            <label className="flex flex-col items-center justify-center aspect-square bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl cursor-pointer hover:border-primary dark:hover:border-golden hover:bg-primary/5 dark:hover:bg-golden/5 transition-all group">
                                <Upload size={22} className="text-gray-400 group-hover:text-primary dark:group-hover:text-golden mb-1.5 transition-colors" />
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 group-hover:text-primary dark:group-hover:text-golden transition-colors">Add Photo</span>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*"
                                    multiple
                                    onChange={handleImageChange}
                                />
                            </label>
                        </div>

                        <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                            <Info size={11} />
                            First image will be the main display photo. Max 20MB per image.
                        </p>

                        {/* Mobile scan hint */}
                        <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border border-purple-200 dark:border-purple-800 rounded-xl">
                            <Smartphone size={16} className="text-purple-500 flex-shrink-0" />
                            <p className="text-xs text-purple-700 dark:text-purple-300">
                                <span className="font-semibold">Pro tip:</span> Use the mobile app to take and upload product photos directly with your camera!
                            </p>
                        </div>
                    </div>
                </SectionCard>

                {/* Action Buttons — sticky at bottom */}
                <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-800 px-4 py-3 flex gap-3 max-w-2xl mx-auto">
                    {isEditMode && (
                        <button
                            type="button"
                            disabled={isLoading}
                            onClick={handleDelete}
                            className="p-3 border-2 border-red-100 dark:border-red-900/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                            title="Delete Product"
                        >
                            <Trash2 size={20} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => navigate(targetBusinessId ? `/business/${targetBusinessId}/products` : '/business')}
                        className="flex-1 py-3 px-4 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="flex-2 flex items-center justify-center gap-2 py-3 px-8 bg-primary dark:bg-golden text-white rounded-xl font-bold hover:opacity-90 disabled:opacity-50 transition-all shadow-lg shadow-primary/20 dark:shadow-golden/20 min-w-[160px]"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                {uploading ? 'Uploading...' : 'Saving...'}
                            </>
                        ) : (
                            <>
                                {isEditMode ? '✓ Update Product' : '+ Create Product'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};
